import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Button
} from '@mui/material';
import {
  Add as AddIcon,
  ChatBubbleOutline as ChatIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';
import { chatTheme } from '../../theme/chatTheme';
import { formatDistanceToNow } from 'date-fns';

const ChatList = ({ mode, weddingId, businessId, selectedChatId, onSelectChat, onNewChat, onChatUpdated, fullWidth = false }) => {
  const _styles = useThemeStyles();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadChats();
  }, [mode, weddingId, businessId]);

  // Reload chats when onChatUpdated is called (from parent)
  useEffect(() => {
    if (onChatUpdated) {
      // Reload when selectedChatId changes (which happens after updates)
      loadChats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [selectedChatId]);

  const loadChats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/chat/conversations');
      
      // Filter for AI chats only
      const aiChats = response.data.filter(chat => 
        chat.type === 'ai-couple' || chat.type === 'ai-supplier'
      );
      
      // Sort by last activity (most recent first)
      aiChats.sort((a, b) => {
        const aTime = new Date(a.lastActivity || a.updatedAt || 0).getTime();
        const bTime = new Date(b.lastActivity || b.updatedAt || 0).getTime();
        return bTime - aTime;
      });
      
      setChats(aiChats);
    } catch (error) {
      setChats([]);
    } finally {
      setLoading(false);
    }
  };



  const getChatPreview = (chat) => {
    if (!chat.messages || chat.messages.length === 0) {
      return 'New chat';
    }
    const lastMessage = chat.messages[chat.messages.length - 1];
    const content = lastMessage.content || '';
    return content.length > 50 ? content.substring(0, 50) + '...' : content;
  };

  const _getChatTime = (chat) => {
    const time = chat.lastActivity || chat.updatedAt || chat.createdAt;
    if (!time) return '';
    try {
      return formatDistanceToNow(new Date(time), { addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, px: fullWidth ? 0 : 2, width: '100%' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Chats
        </Typography>
        <Tooltip title="Start new chat">
          <IconButton size="small" onClick={onNewChat}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : chats.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <ChatIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No chats yet
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={onNewChat}
          >
            Start New Chat
          </Button>
        </Box>
      ) : (
        <List sx={{ p: 0, px: fullWidth ? 0 : 2, width: '100%' }}>
          {chats.map((chat) => (
            <ListItem
              key={chat._id}
              onClick={() => onSelectChat(chat._id)}
              sx={{
                marginBottom: '5px',
                ...chatTheme.sidebar.conversationItem,
                ...(selectedChatId === chat._id && { '&.active': chatTheme.sidebar.conversationItem['&.active'] }),
                backgroundColor: selectedChatId === chat._id ? 'rgba(239, 65, 146, 0.08)' : 'transparent',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.5,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: selectedChatId === chat._id ? 600 : 500,
                  color: selectedChatId === chat._id ? '#ef4192' : '#2C3E50',
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {chat.title || 'Untitled Chat'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {getChatPreview(chat)}
              </Typography>
            </ListItem>
          ))}
        </List>
      )}
    </>
  );
};

export default ChatList;
